import React, { useState, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import Icon from "./Icon/Icon";
import InputField from "./InputField";
import InputPhone from "./InputPhone";

/**
 * Represents a tenant's basic information.
 *
 * @property {string} firstName - The tenant's first name.
 * @property {string} lastName - The tenant's last name.
 * @property {string} email - The tenant's email address.
 * @property {string} mobile - The tenant's mobile phone number.
 */
interface Tenant {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  mobile: string;
}

/**
 * Props for the TenantForm component.
 *
 * @property {boolean} [allowAddTenant] - Determines if adding a new tenant is allowed.
 * @property {number} [startIndex] - The starting index for tenant entries.
 * @property {(name: string) => any} [register] - Function to register form fields, typically from react-hook-form.
 */
type TenantFormProps = {
  allowAddTenant?: boolean;
  startIndex?: number;
  register?: (name: string, options?: any) => any;
  maximumTenants: number;
  smsNotification: boolean;
};

/**
 * Renders a dynamic form for managing tenant information.
 *
 * Allows users to add or remove tenant entries, each with fields for first name, last name,
 * email address, and mobile number. Integrates with form registration (e.g., React Hook Form)
 * via the `register` prop for controlled form handling.
 *
 * @component
 * @param {TenantFormProps} props - The props for TenantForm.
 * @param {boolean} [props.allowAddTenant=true] - Whether to allow adding/removing tenants.
 * @param {number} [props.startIndex=1] - The starting index for tenant labels.
 * @param {Function} props.register - Form registration function for input fields.
 *
 * @returns {JSX.Element} The rendered tenant form component.
 */
const TenantForm: React.FC<TenantFormProps> = ({
  allowAddTenant = true,
  startIndex = 1,
  register,
  maximumTenants,
  smsNotification,
}) => {
  const { unregister, getValues } = useFormContext();
  const [nextId, setNextId] = useState(2);
  const [tenants, setTenants] = useState<Tenant[]>([
    { id: 1, firstName: "", lastName: "", email: "", mobile: "" },
  ]);
  const [initialized, setInitialized] = useState(false);

  /**
   * Initialize tenants state from saved form values
   * This ensures that when navigating back to stage 3, previously added co-tenants are shown
   */
  useEffect(() => {
    if (initialized) return; // Only run once

    const formValues = getValues();
    if (formValues && formValues.tenants) {
      // Get all tenant IDs from form values
      const tenantIds = Object.keys(formValues.tenants).map(id => parseInt(id)).sort((a, b) => a - b);

      if (tenantIds.length > 0) {
        // Reconstruct tenants array from form values
        const loadedTenants = tenantIds.map(id => ({
          id,
          firstName: formValues.tenants[id]?.firstName || "",
          lastName: formValues.tenants[id]?.lastName || "",
          email: formValues.tenants[id]?.email || "",
          mobile: formValues.tenants[id]?.mobile || "",
        }));

        setTenants(loadedTenants);
        // Set nextId to be higher than any existing tenant ID
        const maxId = Math.max(...tenantIds);
        setNextId(maxId + 1);
      }
    }

    setInitialized(true);
  }, [getValues, initialized]);

  const addTenant = () => {
    if (!allowAddTenant) return;
    if (tenants.length >= maximumTenants) return; // Maximum 4 tenants
    setTenants([
      ...tenants,
      { id: nextId, firstName: "", lastName: "", email: "", mobile: "" },
    ]);
    setNextId(nextId + 1);
  };

  const removeTenant = (id: number) => {
    if (!allowAddTenant) return;
    // Unregister the fields for the tenant being removed
    unregister(`tenants.${id}.firstName`);
    unregister(`tenants.${id}.lastName`);
    unregister(`tenants.${id}.email`);
    unregister(`tenants.${id}.mobile`);
    setTenants(tenants.filter((t) => t.id !== id));
  };

  return (
    <div className="d-flex flex-column gap-20px">
      {tenants.map((tenant, index) => {
        const labelIndex = startIndex + index;
        return (
          <React.Fragment key={tenant.id}>
            <div className="d-flex flex-column flex-md-row gap-20px">
              <div className="d-flex flex-column gap-10px flex-fill">
                <div className="d-flex flex-column flex-md-row gap-10px gap-md-20px">
                  <div className="w-100">
                    <InputField
                      id={`firstName-${tenant.id}`}
                      name={`tenants.${tenant.id}.firstName`}
                      label={`Tenant ${labelIndex} First Name`}
                      placeholder="Enter first name"
                      variant="editable"
                      as="input"
                      {...(register &&
                        register(`tenants.${tenant.id}.firstName`, {
                          required: "Tenant first name is required",
                          pattern: {
                            value: /^[A-Za-z0-9 ]+$/,
                            message: "Only letters, numbers and spaces are allowed.",
                          },
                        }))}
                    />
                  </div>
                  <div className="w-100">
                    <InputField
                      id={`lastName-${tenant.id}`}
                      name={`tenants.${tenant.id}.lastName`}
                      label={`Tenant ${labelIndex} Last Name`}
                      placeholder="Enter last name"
                      variant="editable"
                      as="input"
                      {...(register &&
                        register(`tenants.${tenant.id}.lastName`, {
                          required: "Tenant last name is required",
                          pattern: {
                            value: /^[A-Za-z0-9 ]+$/,
                            message: "Only letters, numbers and spaces are allowed.",
                          },
                        }))}
                    />
                  </div>
                </div>
                <div className="d-flex flex-column flex-md-row gap-10px gap-md-20px">
                  <div className="w-100">
                    <InputField
                      id={`email-${tenant.id}`}
                      name={`tenants.${tenant.id}.email`}
                      label={`Tenant ${labelIndex} Email Address`}
                      placeholder="Enter email address"
                      variant="editable"
                      as="input"
                      {...(register &&
                        register(`tenants.${tenant.id}.email`, {
                          required: "Tenant email is required",
                          pattern: {
                            value: /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/,
                            message: "Enter a valid email address.",
                          },
                        }))}
                    />
                  </div>
                  <div className="w-100">
                    {/* <InputField
                      id={`mobile-${tenant.id}`}
                      name={`tenants.${tenant.id}.mobile`}
                      label={`Tenant ${labelIndex} Mobile Number`}
                      placeholder="Enter mobile number"
                      variant="editable"
                      as="input"
                        {...(register &&
                        register(`tenants.${tenant.id}.mobile`, {
                          required: "Tenant mobile number is required",
                          minLength: {
                          value: 8,
                          message: "Invalid mobile number.",
                          },
                          maxLength: {
                          value: 15,
                          message: "Invalid mobile number.",
                          },
                          pattern: {
                          value: /^[+-]?[0-9]{1,14}$/,
                          message:
                            "Mobile number can only start with + or -, followed by digits (max 15 characters).",
                          }
                        }))}
                    /> */}
                    <InputPhone
                      name={`tenants.${tenant.id}.mobile`}
                      country="au"
                      label={`Tenant ${labelIndex} Mobile Number${!smsNotification ? " (optional)" : ""}`}
                      smsNotification={smsNotification}
                      isTenantForm={true}
                      rules={{
                        validate: (value: string) => {
                          if (smsNotification || value?.length > 3) {
                            if (value.replace(/\D/g, "").length < 8) return "Mobile Number is too short";
                            if (value.replace(/\D/g, "").length > 15) return "Mobile Number is too long";
                          }
                          return true;
                        }
                      }}
                    />
                  </div>
                </div>
              </div>
              {allowAddTenant && (
                <div className="d-flex flex-column align-items-end gap-3px">
                  <label htmlFor="" className="d-none d-md-block">
                    &nbsp;
                  </label>
                  <button
                    type="button"
                    className={`btn-theme bg-white px-0 ${
                      tenants.length === 1
                        ? "text-disabled"
                        : "text-grey-2 hover:text-black"
                    }`}
                    onClick={() => removeTenant(tenant.id)}
                    disabled={
                      tenants.length === 1
                    }
                  >
                    <Icon
                      name="remove"
                      size={24}
                      color={tenants.length === 1 ? "disabled" : ""}
                    />
                    Remove
                  </button>
                </div>
              )}
            </div>
            {allowAddTenant && <div className="bg-lines min-h-1px"></div>}
          </React.Fragment>
        );
      })}

      {allowAddTenant && (
        <div className="d-flex">
          <button
            type="button"
            className={`btn-theme bg-white text-grey-2 hover:text-black px-0 h-auto ${tenants.length === maximumTenants ? "d-none" : "" }`}
            onClick={addTenant}
            disabled={tenants.length === maximumTenants}
          >
            <Icon name="add" />
            Add Another Tenant
          </button>
        </div>
      )}
    </div>
  );
};

export default TenantForm;
